import { __ } from "@wordpress/i18n";
import { Modal, Notice } from "@wordpress/components";
import { useEffect, useMemo, useState } from "@wordpress/element";
import { HiOutlineHandThumbUp } from "react-icons/hi2";
import { HashLoader } from "react-spinners";
import { ajax } from "../../config/axios";
import { $, AJAX_ACTION, INPUT, NONCE, OPTIONS } from "../../utils/constants";
import { css } from "@/utils/css";

type FormType = "topic" | "reply";

type TopicContext = {
    topicId: number;
    topicTitle: string;
    thread: string;
};

type Payload = {
    action: string;
    bbp_forum_id?: string;
    bbp_stick_topic?: string;
    bbp_topic_content?: string;
    bbp_reply_content?: string;
    bbp_topic_status?: string;
    bbp_topic_tags?: string;
    bbp_topic_title?: string;
    _wpnonce: string;
    [key: string]: any;
};

interface Props {
    form: HTMLFormElement;
    formType: FormType;
    topicContext?: TopicContext;
}

export default function AIResponseModal({ form, formType, topicContext }: Props) {
    const data = useMemo(() => {
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        return data as Payload;
    }, [form]);

    const [isOpen, setIsOpen] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [answer, setAnswer] = useState("");
    const [error, setError] = useState("");

    const handleClose = () => {
        setIsOpen(false);
        form.submit();
    };

    const handleThisHelped = async () => {
        $(form).append(`
            <input type="hidden" name="${INPUT.ANTIMANUAL_RESPONSE_HELPED.NAME}" value="${INPUT.ANTIMANUAL_RESPONSE_HELPED.VALUE}" />
            <textarea hidden name="${INPUT.ANTIMANUAL_RESPONSE.NAME}">${answer}</textarea>
        `);
        handleClose();
    };

    // For topics: get title from form or DOM
    // For replies: get title from topic context or DOM
    const topicTitle = useMemo(() => {
        if (formType === "reply" && topicContext?.topicTitle) {
            return topicContext.topicTitle;
        }
        return (
            data.bbp_topic_title ||
            document.querySelector(".bbp-topic-title")?.textContent ||
            document.querySelector(".entry-title")?.textContent ||
            document.querySelector("h1")?.textContent ||
            ""
        );
    }, [data.bbp_topic_title, formType, topicContext]);

    // Get content from the form
    const content = useMemo(() => {
        return data.bbp_topic_content || data.bbp_reply_content || "";
    }, [data.bbp_topic_content, data.bbp_reply_content]);

    // Modal title based on form type
    const modalTitle = formType === "topic" 
        ? __("AI Suggested Response", "antimanual")
        : __("AI Suggested Reply", "antimanual");

    // Continue button text based on form type
    const continueButtonText = formType === "topic"
        ? __("Continue with Ticket", "antimanual")
        : __("Post Without AI Help", "antimanual");

    useEffect(() => {
        // Use different AJAX action based on form type
        if (formType === "topic") {
            ajax<string>({
                nonce: NONCE.AI_RESPONSE_TO_TOPIC,
                action: AJAX_ACTION.AI_RESPONSE_TO_TOPIC,
                forum_id: data.bbp_forum_id || "0",
                topic_title: topicTitle,
                topic_content: content,
            })
                .then((answer) => setAnswer(answer))
                .catch((error) => {
                    setError(error?.message);
                })
                .finally(() => setIsLoading(false));
        } else {
            // Reply - use the reply-specific endpoint with thread context
            ajax<string>({
                nonce: NONCE.AI_RESPONSE_TO_REPLY,
                action: AJAX_ACTION.AI_RESPONSE_TO_REPLY,
                topic_title: topicTitle,
                reply_content: content,
                thread_context: topicContext?.thread || "",
            })
                .then((answer) => setAnswer(answer))
                .catch((error) => {
                    // For "reply_too_short" error, just close and submit
                    if (error?.code === "reply_too_short") {
                        setIsOpen(false);
                        form.submit();
                        return;
                    }
                    setError(error?.message);
                })
                .finally(() => setIsLoading(false));
        }
    }, [data, topicTitle, content, formType, topicContext]);

    if (!isOpen || !topicTitle || !content) {
        return null;
    }

    return (
        <Modal title={modalTitle} onRequestClose={handleClose}>
            <div className={css("w-700 max-w-98vw p-0")}>
                {isLoading ? (
                    <div className={css("min-h-75 flex flex-col items-center justify-center gap-5 p-5")}>
                        <HashLoader color="#4e5760" />
                        <h4 className={css("text-dark")}>{__("AI is cooking...", "antimanual")}</h4>
                    </div>
                ) : !answer ? (
                    <Notice status="error" isDismissible={false}>
                        {error || __("Something went wrong", "antimanual")}
                    </Notice>
                ) : (
                    <>
                        <div className={css("bg- text-primary rounded-2 py-4 px-5 mb-6 font-normal be-cautious")}>
                            <span className={css("text-uppercase font-semibold letter-spacing-05")}>{__("BE CAUTIOUS:", "antimanual")}</span>{" "}
                            <span className={css("font-normal text-black")}>{__(OPTIONS.AI_RESPONSE_NOTICE, "antimanual")}</span>
                        </div>
                        <div
                            className={css("bg-white rounded-2 py-6 px-5 text-md text-black ai-response-text")}
                            dangerouslySetInnerHTML={{ __html: answer }}
                        />
                        <div className={css("flex justify-end gap-3 mt-8")}>
                            <button
                                className={css("border-none rounded-1 py-4 px-6 cursor-pointer flex items-center gap-2 this-helped")}
                                onClick={handleThisHelped}
                            >
                                {__("This helped!", "antimanual")}
                                <HiOutlineHandThumbUp />
                            </button>
                            <button
                                className={css("text-white text-sm bg-dark border-none rounded-1 py-4 px-6 cursor-pointer continue-ticket")}
                                onClick={handleClose}
                            >
                                {continueButtonText}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}
